import { Link } from 'react-router-dom';
import Button from '@/components/base/Button';
import { Field, SelectField, TextareaField, CheckboxField, Honeypot, FormStatus } from '@/components/base/FormFields';
import PageHero from '@/components/feature/PageHero';
import { useFormSubmit } from '@/hooks/useFormSubmit';
import { ENQUIRY_TYPES, CONTACT, SERVICES } from '@/data/site';

export default function Contact() {
  const { status, submitting, errorMessage, handleSubmit } = useFormSubmit();

  return (
    <>
      <PageHero
        eyebrow="Contact"
        title="Let&rsquo;s Connect"
        intro={
          <>
            <p className="font-label text-sm uppercase tracking-[0.14em] text-accent-600 mb-3">
              Have something in mind? We&rsquo;d love to hear about it.
            </p>
            <p>
              Whether you&rsquo;re planning a special event, looking for a supplier, interested in
              joining our network or simply have a question, KOR38 is here to help. Tell us a little
              about what you&rsquo;re looking for, and Daniella will personally review your enquiry.
            </p>
          </>
        }
        imageSrc="https://readdy.ai/api/search-image?query=stylish%20private%20celebration%20party%20with%20softly%20glowing%20warm%20lights%2C%20elegant%20diverse%20guests%20raising%20glasses%2C%20refined%20luxury%20event%20photography%2C%20dark%20sophisticated%20ambience%20with%20golden%20highlights%2C%20joyful%20welcoming%20atmosphere&width=1600&height=760&seq=kor38-contact-hero&orientation=landscape"
        imageAlt="Stylish private celebration with warm lighting and guests raising glasses"
      />

      <section className="w-full px-4 md:px-8 lg:px-12 pb-20 md:pb-28 bg-background-50">
        <div className="max-w-content mx-auto grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12">
          <div className="lg:col-span-7">
            <form
              id="contact_enquiry"
              name="contact_enquiry"
              data-netlify="true"
              data-readdy-form="contact_enquiry"
              method="post"
              onSubmit={(e) => void handleSubmit(e, 'https://readdy.ai/api/form/db6hqckbe24agvuu3jrg')}
              className="rounded-lg border border-background-200 bg-background-100 p-5 md:p-8 space-y-6"
            >
              <input type="hidden" name="form-name" value="contact_enquiry" />
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <Field
                  label="First name"
                  name="first_name"
                  required
                  autoComplete="given-name"
                  placeholder="Your first name"
                />
                <Field
                  label="Last name"
                  name="last_name"
                  required
                  autoComplete="family-name"
                  placeholder="Your last name"
                />
                <Field
                  label="Business name (optional)"
                  name="business_name"
                  autoComplete="organization"
                  placeholder="If enquiring on behalf of a business"
                />
                <Field label="Email" name="email" type="email" required autoComplete="email" placeholder="you@example.com" />
                <Field label="Telephone (optional)" name="telephone" type="tel" autoComplete="tel" placeholder="Your contact number" />
                <SelectField label="Enquiry type" name="enquiry_type" options={ENQUIRY_TYPES} required />
              </div>

              <TextareaField
                label="Your message"
                name="message"
                required
                rows={6}
                placeholder="Tell us a little about what you're looking for."
              />

              <div className="space-y-4 pt-2">
                <CheckboxField
                  name="privacy_ack"
                  required
                  label={
                    <>
                      I have read and understand the{' '}
                      <Link to="/privacy-policy" className="text-primary-300 underline underline-offset-2">
                        Privacy Policy
                      </Link>{' '}
                      and agree to KOR38 using my details to respond to this enquiry.{' '}
                      <span className="text-primary-400">*</span>
                    </>
                  }
                />
                <CheckboxField
                  name="marketing_consent"
                  label="I would like to receive occasional news and offers from KOR38. (Optional)"
                />
              </div>

              <Honeypot />

              <div className="space-y-4">
                <FormStatus
                  status={status}
                  successMessage="Thank you for your enquiry. Daniella will personally review your details and contact you about the next steps."
                  errorMessage={errorMessage}
                />
                <Button type="submit" variant="primary" size="lg" disabled={submitting} className="w-full sm:w-auto">
                  {submitting ? 'Sending…' : 'Send My Enquiry'}
                </Button>
              </div>
            </form>
          </div>

          <aside className="lg:col-span-5 space-y-6">
            <div className="rounded-lg border border-background-200 bg-background-100 p-6">
              <h2 className="font-heading text-lg text-foreground-950">Location</h2>
              <ul className="mt-4 space-y-4 text-[15px] text-foreground-600">
                <li className="flex items-start gap-3">
                  <i className="ri-map-pin-line text-lg text-primary-400 mt-0.5" aria-hidden="true"></i>
                  <span>
                    {CONTACT.location}
                    <br />
                    Serving {CONTACT.serviceArea}
                  </span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-mail-line text-lg text-primary-400 mt-0.5" aria-hidden="true"></i>
                  <span>
                    <a href={`mailto:${CONTACT.email}`} className="text-foreground-800 hover:text-primary-300">
                      {CONTACT.email}
                    </a>
                    <span className="block text-sm text-foreground-600">General and privacy enquiries</span>
                  </span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-mail-send-line text-lg text-primary-400 mt-0.5" aria-hidden="true"></i>
                  <span>
                    <a href={`mailto:${CONTACT.supplierEmail}`} className="text-foreground-800 hover:text-primary-300">
                      {CONTACT.supplierEmail}
                    </a>
                    <span className="block text-sm text-foreground-600">Supplier enquiries</span>
                  </span>
                </li>
              </ul>
            </div>

            <div className="rounded-lg bg-[#1B1B1B] border border-white/10 p-6">
              <h2 className="font-heading text-lg text-foreground-950">Ready to make a request?</h2>
              <p className="mt-3 text-base leading-relaxed text-foreground-600">
                Our dedicated enquiry forms capture everything we need to help you faster.
              </p>
              <ul className="mt-5 space-y-3">
                {SERVICES.map((service) => (
                  <li key={service.id}>
                    <Link
                      to={service.path}
                      className="flex items-center justify-between gap-3 text-[15px] text-foreground-700 hover:text-primary-300 transition-colors duration-300"
                    >
                      <span>{service.name}</span>
                      <i className="ri-arrow-right-line text-primary-400" aria-hidden="true"></i>
                    </Link>
                  </li>
                ))}
                <li>
                  <Link
                    to="/apply"
                    className="flex items-center justify-between gap-3 text-[15px] text-foreground-700 hover:text-primary-300 transition-colors duration-300"
                  >
                    <span>Supplier application</span>
                    <i className="ri-arrow-right-line text-primary-400" aria-hidden="true"></i>
                  </Link>
                </li>
              </ul>
            </div>
          </aside>
        </div>
      </section>
    </>
  );
}